<script setup lang="ts">
import type { GameDepositApprovedInfo } from '~/composables/useGameDepositModal';

// `info` só existe quando o depósito foi aprovado com dados de conta bancária
// na resposta do /wallet/charge. Sem ela a tela é a confirmação genérica de
// sempre — é o que acontece nos provedores que não devolvem conta.
type Props = {
  info?: GameDepositApprovedInfo | null;
};

const { info = null } = defineProps<Props>();

type Emits = {
  close: [];
};

const emit = defineEmits<Emits>();
</script>

<template>
  <div class="gdm-success">
    <div class="gdm-success-msg">
      <NuxtIcon
        class="gdm-success-icon"
        name="check_success"
      />

      <p class="gdm-success-title">
        Depósito confirmado!
        <br />
        O valor está disponível para uso.
      </p>

      <p class="gdm-success-history">
        Acesse os dados da transação no seu
        <strong>histórico</strong>
      </p>
    </div>

    <!-- Conta bancária que pagou: o outro lado do consentimento
         `save_bank_account` — quem autorizou o cadastro precisa ver QUAL conta
         foi. Mesmo conteúdo do fluxo antigo
         (components/deposit/result/Approved.vue). -->
    <p
      v-if="info?.differentAccount"
      class="gdm-success-notice"
    >
      {{ $t('account.wallet.deposit_approved.different_account_notice') }}
    </p>

    <div
      v-if="info?.bankAccountLabel"
      class="gdm-success-account"
    >
      <p class="gdm-success-account-badge">
        ✓
        {{
          info.isNewAccount
            ? $t('account.wallet.deposit_approved.account_badge')
            : $t('account.wallet.deposit_approved.account_badge_existing')
        }}
      </p>
      <p class="gdm-success-account-bank">{{ info.bankAccountLabel }}</p>
      <p class="gdm-success-account-line">{{ info.agencyAccountLabel }}</p>
    </div>

    <button
      class="gdm-success-close"
      type="button"
      @click="emit('close')"
    >
      FECHAR
    </button>
  </div>
</template>

<style scoped lang="scss">
$green: #3eff6e;

.gdm-success {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  font-family: 'Plus Jakarta Sans', sans-serif;
}

.gdm-success-msg {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 5px;
  width: 100%;
  min-height: 150px;
  text-align: center;
}

.gdm-success-icon {
  font-size: 42px;
  color: $green;
  line-height: 1;

  :deep(svg) {
    width: 42px;
    height: 42px;
  }
}

.gdm-success-title {
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  color: $green;
}

.gdm-success-history {
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  color: #b4b7bd;

  strong {
    color: #fff;
    font-weight: 700;
  }
}

.gdm-success-notice {
  color: rgba(255, 255, 255, 0.6);
  font-size: 12px;
  line-height: 16px;
  text-align: center;
}

.gdm-success-account {
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
  padding: 12px;
  border: 1px solid #28282c;
  border-radius: 12px;
  text-align: left;
}

.gdm-success-account-badge {
  color: $green;
  font-size: 11px;
  font-weight: 700;
}

.gdm-success-account-bank {
  color: #fff;
  font-size: 13px;
  font-weight: 600;
}

.gdm-success-account-line {
  color: rgba(255, 255, 255, 0.6);
  font-size: 12px;
}

.gdm-success-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 41px;
  border-radius: 12px;
  border: 1px solid #0c1b44;
  background: #08112d;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  backdrop-filter: blur(10px);
  transition: background 0.15s ease;

  &:hover {
    background: #0c1b44;
  }
}
</style>
